// PlaybookScorecard (lane B14, brief §2.1, DC-244/245/246, contract C6): the Corporate Interview Playbook as the
// scorecard's ONE frame, mounted by both scorecard routes — `#interviews/<pk>/<app>` (ScorecardPage, in its McRoot) and
// `#packets/<pk>/<app>` (Packets.tsx, inside its McScope) — so an interviewer arriving from My Interviews, the Action
// Center, a reminder or an email sees the same page. Laid out as Tejesh asked on 26 Oct (09:02): the whole guide in ONE
// collapsed card at the top (how the 1–5 scale works, what the evidence speaks to, the rules, the bands, one sample
// answer), then one clean question → answer card per value and per skill (the long standard behind a small disclosure),
// then the closing cards — the recommendation, the Code of Conduct, Sign, the Gate and Submit — and the read-only cards
// after submission. The hosts own fetching, autosave, conflicts, recovery and the revision dialog.
//
// Controlled and computing nothing the server decides (Law 3): the offered options are `level.offered` in its order,
// the lock is the server's level block (never a client count), the average core score and the rubric band are the
// server's, the open items are the server's `missing[]`. DOM order is reading order is keyboard order at every width
// (DS row 124, WCAG 2.4.3): the cards sit in the grammar's CardGrid in their reading order, never re-ordered by CSS.

import { useEffect, useState, type FocusEvent, type MouseEvent, type ReactNode } from "react";
import type { RatingInput, ScorecardOpenOutput } from "@/api/tools";
import {
  ACCESS_LABELS, Banner, Button, Card, CheckRow, Checklist, Disclosure, EVIDENCE_ROWS, Field, Flex, Gate, Icon, Input, KeyValue, LEVEL_RULE_LABELS,
  LevelRule, PLAYBOOK_LABELS, Pill, PlaybookRubric, ROLE_LABELS, RatingScale, RecoTiles, SCORECARD_LABELS, SCORECARD_STATUS, ACTION_LABELS,
  Segmented, Select, Stack, TextArea, Toggle, cx, formatDate, playbookRadioName, recommendationWord, useViewerMemory,
  type GateRow, type LevelRuleBand, type RatingValue, type ScoreValue,
} from "@/mc";
import { CardGrid, GridCell, RecordCard, SectionTabs } from "@/mc";
import { PLAN_WORDS } from "@/views/plan/labels";
import {
  SCORE_LABELS, SCORECARD_PAGE_LABELS, FRAME_LABELS, FRAME_TEMPLATES, GUIDE_LABELS, KICKOFF_CONTENT_LABELS, UNASSESSED_REASONS, COC_ABOUT,
  V1_FIELD_WORDS,
} from "./labels";
import {
  asksFollowUp, completedPages, dimensionLabel, dimensionsOf, fieldId, gateItems, orderedDimensions, pagesOf,
  playbookOf, ratingRow, ratingValueOf, recoRows, setRating, shownRecommendation, skillTitlesOf, type Band, type Dimension, type Draft,
  type MissingItem, type PageDef, type PlaybookGuide, type PlaybookLevel, type PlaybookSkillPageData, type PlaybookValuePageData,
  type ScorecardMode,
} from "./playbook";

export interface PlaybookScorecardProps {
  /** `scorecard.open` (C1), with `guide` resolved by the host when the projection carries none. */
  open: ScorecardOpenOutput;
  draft: Draft;
  onChange(next: Draft): void;
  mode: ScorecardMode;
  editable: boolean;
  /** Paper mode: the Recruiter keys a signed sheet, so Sign is not drawn (Submit sends `attested` true). */
  hideAttestation?: boolean | undefined;
  /** The server's open items (C1); the host passes none where the server cannot know them (a revision, a paper record). */
  missing: readonly MissingItem[];
  onSave?: (() => void) | undefined;
  onSubmit?: (() => void) | undefined;
  /** The verb on the Gate's action (Submit scorecard · Submit revision · Record for …). */
  submitLabel?: string | undefined;
  submitBusy?: boolean | undefined;
  saveBusy?: boolean | undefined;
  saveState?: ReactNode;
  notesRail?: ReactNode;
  printAction?: ReactNode;
  /** A refusal's words by field id (the server's field names are the C1 ids): drawn on the field it names. */
  errors?: Readonly<Record<string, string>> | undefined;
  /** A refusal that names no field. */
  refusal?: string | null | undefined;
  /** F-185: the host draws the record header (RecordPage), so the frame draws no heading and no print of its own. */
  framed?: boolean | undefined;
  /** The packet's kickoff reminders (`packet.open` reminders, server text): the guide card's first part. */
  reminders?: readonly string[] | undefined;
}

const SUBMITTED = new Set(["submitted", "frozen", "frozen_with_addenda"]);
const RULE_BAND: Readonly<Record<"below_vp" | "vp_plus", LevelRuleBand>> = { below_vp: "sr_director_and_below", vp_plus: "vp_plus" };
const FOCUSABLE = "[role=radio][tabindex='0'], input:not([type=hidden]), textarea, select, button, [tabindex='0']";

/** Moves focus to the control an open item names (the Gate's rows, the stepper's chips). */
export function focusField(id: string): boolean {
  if (typeof document === "undefined" || !id) return false;
  const target = document.getElementById(id);
  if (!target) return false;
  const control = target.matches(FOCUSABLE) ? target : target.querySelector<HTMLElement>(FOCUSABLE) ?? target;
  control.focus({ preventScroll: true });
  const reduce = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  target.scrollIntoView?.({ behavior: reduce ? "instant" : "smooth", block: "center" });
  return true;
}

/** Runs after the next paint (a page shown by state is laid out before focus moves into it). */
function afterPaint(run: () => void) {
  if (typeof window === "undefined") return;
  window.requestAnimationFrame(() => window.requestAnimationFrame(run));
}

/** The blocked rows' reason: the binding rule's own sentence (the VP+ banner's; the legacy guide's level sentence). */
function lockReason(level: PlaybookLevel, guide: PlaybookGuide | null): string {
  if (level.band === "vp_plus") return LEVEL_RULE_LABELS.vpRule;
  return guide?.recommendation_page.decision_rule_text ?? LEVEL_RULE_LABELS.vpRule;
}

/** The 1–5 descriptors for the radios' names (C6: "<n> — <descriptor>"). */
function descriptorsOf(guide: PlaybookGuide | null): Record<"1" | "2" | "3" | "4" | "5", string> {
  const d = guide?.score_descriptions ?? {};
  return { 1: d["1"] ?? "", 2: d["2"] ?? "", 3: d["3"] ?? "", 4: d["4"] ?? "", 5: d["5"] ?? "" };
}

function fallbackValuePage(d: Dimension): PlaybookValuePageData {
  return { key: d.key, name: dimensionLabel(d) ?? d.key, page_title: null, assesses: "", questions: [], strong: "", weak: "", anchors: {}, evidence_prompt: "" };
}

function fallbackSkillPage(d: Dimension, titles: Readonly<Record<string, string>>): PlaybookSkillPageData {
  return { number: Number(d.key), title: titles[d.key] ?? "", what_strong_looks_like: "", prompts: [], fit_label: null };
}

/** The read-only header box of page 1: Candidate · Title · Interviewer · Date (never typed). */
function HeaderBox({ open, guide }: { open: ScorecardOpenOutput; guide: PlaybookGuide | null }) {
  const held = open.held_at ? formatDate(open.held_at) : null;
  return (
    <div className="mc-pbs-head" id="round_held" tabIndex={-1}>
      <KeyValue
        cols={2}
        items={[
          [SCORE_LABELS.candidate, open.candidate],
          [FRAME_LABELS.title, guide?.header.target_role ?? "—"],
          [ROLE_LABELS.interviewer, open.author_name || "—"],
          [FRAME_LABELS.date, held ?? "—"],
        ]}
      />
    </div>
  );
}

/** F-185: the pages as the record's section tabs — one numbered step per page, done when nothing is open on it, the
 * current one shown — with *Page n of N* and the status pill at the row's end. */
function Stepper({ pages, current, done, status, onGo }: {
  pages: readonly PageDef[]; current: number; done: ReadonlySet<string>; status: ReactNode; onGo(page: PageDef): void;
}) {
  return (
    <SectionTabs
      label={SCORE_LABELS.scorecard}
      className="mc-pbs-stepper"
      steps={pages.map((page) => ({ key: page.key, label: page.label, done: done.has(page.key), controls: page.anchor }))}
      active={current}
      onSelect={(at) => { const page = pages[at]; if (page) onGo(page); }}
      aside={<><span className="mc-pbs-pageof" aria-live="polite">{FRAME_TEMPLATES.pageOf({ n: current + 1, of: pages.length })}</span>{status}</>}
    />
  );
}

/** The Unassessed reason (a value page's slot): his four reasons, and Other's own words. */
function UnassessedReason({ d, row, editable, invalid, onRow }: {
  d: Dimension; row: RatingInput; editable: boolean; invalid: boolean; onRow(next: RatingInput): void;
}) {
  const reason = row.not_assessed_reason ?? "";
  const other = reason === "other" || reason.startsWith("Other:");
  return (
    <Stack gap={8}>
      <Field label={V1_FIELD_WORDS.unassessedReason} required id={fieldId("not_assessed_reason", d)} error={invalid ? V1_FIELD_WORDS.completeField : undefined}>
        <Select
          value={other ? "other" : reason}
          mode={editable ? undefined : "view"}
          options={[
            { value: "", label: "—" },
            ...(Object.keys(UNASSESSED_REASONS) as (keyof typeof UNASSESSED_REASONS)[]).map((key) => ({ value: key, label: UNASSESSED_REASONS[key] })),
          ]}
          onChange={(value: string) => onRow({ ...row, not_assessed_reason: value || null })}
        />
      </Field>
      {other ? (
        <Field label={V1_FIELD_WORDS.otherReason} required>
          <TextArea
            rows={2}
            maxLength={393}
            readOnly={!editable}
            value={reason.startsWith("Other:") ? reason.slice(7) : ""}
            onChange={(event) => onRow({ ...row, not_assessed_reason: `Other: ${event.target.value}` })}
          />
        </Field>
      ) : null}
    </Stack>
  );
}

/** "Your notes" (the author's private notes, DS Q-E26): the right rail on desktop, under the main column at ≤ 1180. */
export function NotesRail({ value, disabled, onChange }: { value: string | null | undefined; disabled?: boolean | undefined; onChange?: ((next: string) => void) | undefined }) {
  return (
    <Card title={FRAME_LABELS.yourNotes} className="mc-pbs-notes">
      {onChange ? (
        <TextArea label={FRAME_LABELS.yourNotes} rows={12} value={value ?? ""} readOnly={disabled} onChange={(event) => onChange(event.target.value)} />
      ) : (
        <p className="mc-pbs-ro">{value || "—"}</p>
      )}
    </Card>
  );
}


const GUIDE_MEMORY = "scorecard.guide";
const SCALE_DOWN = ["5", "4", "3", "2", "1"] as const;

/** The anchor every one of the card's values gives a number (the guide's shared 4 / 3 / 2 lines), else none. */
function sharedAnchor(pages: readonly PlaybookValuePageData[], n: string): string | null {
  const first = pages[0]?.anchors[n];
  return first && pages.every((page) => page.anchors[n] === first) ? first : null;
}

/** Two sentences say the same thing when their words match (case, spacing and end punctuation aside). */
function sameWords(text: string): string {
  return text.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim();
}

/** The 5 = Strong and 1 = Weak words of one value (the guide's strong / weak). */
function Anchors({ page }: { page: PlaybookValuePageData }) {
  return (
    <div className="mc-pbs-anchors">
      <div className="mc-pbs-a mc-pbs-a-strong">
        <span className="mc-pbs-ah"><Icon name="check" size={14} />{PLAYBOOK_LABELS.strongAnchor}</span>
        <p>{page.strong}</p>
      </div>
      <div className="mc-pbs-a mc-pbs-a-weak">
        <span className="mc-pbs-ah"><Icon name="x" size={14} />{PLAYBOOK_LABELS.weakAnchor}</span>
        <p>{page.weak}</p>
      </div>
    </div>
  );
}

/**
 * The guide card at the top (Tejesh 26 Oct): ONE disclosure, collapsed by default and remembered per viewer, holding the
 * whole guide for this card in reading order — the 1–5 scale and each value's 5 / 1 words, what the evidence speaks to
 * and what a strong observation is, the rules the server applies, the recommendation bands, and one sample answer.
 * Everything in it is the pinned guide the server sent (data, drawn as is) or a label; the sample is text, never a field,
 * so nothing in it is saved or sent.
 */
function GuideCard({ guide, values, skills, descriptors, band, reminders = [] }: {
  guide: PlaybookGuide;
  reminders?: readonly string[] | undefined;
  values: readonly PlaybookValuePageData[];
  skills: readonly PlaybookSkillPageData[];
  descriptors: Readonly<Record<string, string>>;
  band: Band | null;
}) {
  const [open, setOpen] = useViewerMemory<boolean>(GUIDE_MEMORY, false);
  const reco = guide.recommendation_page;
  const sample = values.find((page) => page.questions.length > 0) ?? null;
  const sampleSkill = skills.find((skill) => skill.what_strong_looks_like) ?? null;
  // the rule is the pinned guide's own sentence; on a requisition whose guide carries none, the level's rule
  const levelRule = reco.decision_rule_text ? null : band === "vp_plus"
    ? [LEVEL_RULE_LABELS.vpPlus, LEVEL_RULE_LABELS.vpRule]
    : band === "below_vp" ? [LEVEL_RULE_LABELS.srDirectorAndBelow, LEVEL_RULE_LABELS.srDirectorRule] : null;
  // the kickoff reminders lead the guide; one the guide already says (same words) is kept once, in the guide's place
  const said = new Set([reco.decision_rule_text, reco.attestation_text, reco.coc_question_text, reco.alignment_text,
    PLAN_WORDS.allFiveCovered, SCORECARD_PAGE_LABELS.noPeek].filter((text): text is string => Boolean(text)).map(sameWords));
  const kickoff = reminders.filter((reminder, at) => reminder.trim() && !said.has(sameWords(reminder))
    && reminders.findIndex((other) => sameWords(other) === sameWords(reminder)) === at);
  const options = reco.options.flatMap((option) => {
    const word = recommendationWord(option.key);
    return word ? [{ key: option.key, label: word.label, description: option.description }] : [];
  });
  return (
    <RecordCard variant="form" span="all" className="mc-pbs-card mc-pbs-guide">
      <Disclosure
        title={KICKOFF_CONTENT_LABELS.interviewGuidance}
        sub={guide.page_words?.guide_title ?? guide.header.title ?? undefined}
        open={open}
        onOpenChange={setOpen}
        id="pb-guide"
      >
        <div className="mc-pbs-gd">
          {kickoff.length ? (
            <div className="mc-pbs-gs mc-pbs-kickoff">
              <ol className="mc-pbs-rules">{kickoff.map((reminder) => <li key={reminder}>{reminder}</li>)}</ol>
            </div>
          ) : null}
          <section className="mc-pbs-gs" aria-labelledby="pb-guide-scale">
            <h3 className="mc-pbs-h" id="pb-guide-scale">{SCORECARD_LABELS.rating}</h3>
            <ol className="mc-pbs-scale">
              {SCALE_DOWN.map((n) => {
                const anchor = sharedAnchor(values, n);
                return (
                  <li key={n}>
                    <b>{playbookRadioName(Number(n) as ScoreValue, descriptors[n] || undefined)}</b>
                    {anchor ? <span> · {anchor}</span> : null}
                  </li>
                );
              })}
            </ol>
            {values.map((page) => (
              <div key={page.key} className="mc-pbs-gv">
                <h4 className="mc-pbs-k">{page.name}</h4>
                <Anchors page={page} />
              </div>
            ))}
          </section>

          <section className="mc-pbs-gs" aria-labelledby="pb-guide-evidence">
            <h3 className="mc-pbs-h" id="pb-guide-evidence">{PLAYBOOK_LABELS.requiredObservations}</h3>
            <dl className="mc-pbs-dl">
              {values.map((page) => (
                <div key={page.key}>
                  <dt>{page.name} · {PLAYBOOK_LABELS.standardAssessed}</dt>
                  <dd>{page.assesses}</dd>
                </div>
              ))}
            </dl>
            {skills.length ? (
              <>
                <h4 className="mc-pbs-k">{PLAYBOOK_LABELS.strongObservations}</h4>
                <dl className="mc-pbs-dl">
                  {skills.map((skill) => (
                    <div key={skill.number}>
                      <dt>{skill.title} · {PLAYBOOK_LABELS.whatStrongLooksLike}</dt>
                      <dd>{skill.what_strong_looks_like}</dd>
                    </div>
                  ))}
                </dl>
              </>
            ) : null}
          </section>

          <div className="mc-pbs-gs">
            <ul className="mc-pbs-rules">
              {reco.decision_rule_text ? <li><b>{reco.decision_rule_text}</b></li> : null}
              {levelRule ? <li><b>{levelRule[0]}</b> {levelRule[1]}</li> : null}
              <li>{PLAN_WORDS.allFiveCovered}</li>
              <li><b>{SCORECARD_PAGE_LABELS.independentFeedback}</b> {SCORECARD_PAGE_LABELS.noPeek}</li>
              {reco.attestation_text ? <li>{reco.attestation_text}</li> : null}
              {reco.coc_question_text ? <li>{reco.coc_question_text}{reco.coc_no_instruction ? <> {reco.coc_no_instruction}</> : null}</li> : null}
              {reco.alignment_text ? <li>{reco.alignment_text}</li> : null}
            </ul>
          </div>

          <section className="mc-pbs-gs" aria-labelledby="pb-guide-bands">
            <h3 className="mc-pbs-h" id="pb-guide-bands">{PLAYBOOK_LABELS.scoringSummaryRubric}</h3>
            {guide.rubric.scoring_summary_text ? <p className="mc-pbs-k">{guide.rubric.scoring_summary_text}</p> : null}
            {guide.rubric.lines.length ? <ul className="mc-pbs-bands">{guide.rubric.lines.map((line) => <li key={line}>{line}</li>)}</ul> : null}
            {guide.rubric.bands_note ? <p className="mc-hint">{guide.rubric.bands_note}</p> : null}
            {options.length ? (
              <dl className="mc-pbs-dl">
                {options.map((option) => (
                  <div key={option.key}>
                    <dt>{option.label}</dt>
                    {option.description ? <dd>{option.description}</dd> : null}
                  </div>
                ))}
              </dl>
            ) : null}
          </section>

          {sample ? (
            // the worked example: plain text from the guide (its first question, the 5 = Strong words as the evidence a 5
            // reads like, the 5 descriptor, a skill's strong standard) — no field, never saved, never sent
            <section className="mc-pbs-gs mc-pbs-sample" aria-labelledby="pb-guide-sample">
              <div className="mc-pbs-sample-h">
                <h3 className="mc-pbs-h" id="pb-guide-sample">{GUIDE_LABELS.example}</h3>
                <Pill tone="neutral" dot={false}>{sample.name}</Pill>
              </div>
              <dl className="mc-pbs-dl mc-pbs-sample-b">
                <div>
                  <dt>{PLAYBOOK_LABELS.behavioralQuestions}</dt>
                  <dd>{sample.questions[0]}</dd>
                </div>
                <div>
                  <dt>{PLAYBOOK_LABELS.requiredObservations}</dt>
                  <dd>{sample.anchors["5"] || sample.strong}</dd>
                </div>
                <div>
                  <dt>{SCORECARD_LABELS.rating}</dt>
                  <dd><b>{playbookRadioName(5, descriptors["5"] || undefined)}</b></dd>
                </div>
                {sampleSkill ? (
                  <div>
                    <dt>{sampleSkill.title} · {PLAYBOOK_LABELS.strongObservations}</dt>
                    <dd>{sampleSkill.what_strong_looks_like}</dd>
                  </div>
                ) : null}
              </dl>
            </section>
          ) : null}
        </div>
      </Disclosure>
    </RecordCard>
  );
}

/** Under a rating: the scale's two ends (1 = Weak · 5 = Strong) and the chosen number's descriptor. */
function ScaleWords({ value, descriptors }: { value: RatingValue | null; descriptors: Readonly<Record<string, string>> }) {
  const chosen = typeof value === "number" ? playbookRadioName(value, descriptors[String(value)] || undefined) : null;
  return (
    <p className="mc-pbs-ends">
      <span>{PLAYBOOK_LABELS.weakAnchor}</span>
      {chosen ? <b aria-live="polite">{chosen}</b> : <span aria-hidden="true" />}
      <span>{PLAYBOOK_LABELS.strongAnchor}</span>
    </p>
  );
}

export function PlaybookScorecard({
  open, draft, onChange, mode, editable, hideAttestation, missing, onSave, onSubmit, submitLabel, submitBusy, saveBusy, saveState,
  notesRail, printAction, errors = {}, refusal, framed, reminders,
}: PlaybookScorecardProps) {
  const pb = playbookOf(open);
  const { guide, level } = pb;
  const dims = orderedDimensions(dimensionsOf(open));
  const titles = skillTitlesOf(guide);
  const pages = pagesOf(dims, { skills: FRAME_LABELS.skills, recommendation: SCORECARD_LABELS.recommendation });
  // view: the submitted card read-only (the cards as text); locked: the controls stay but take no input (a submission
  // in flight, a seat that may not write)
  const view = mode === "readonly";
  const locked = view || !editable;
  const shownMissing = hideAttestation ? missing.filter((item) => item.id !== "attested") : missing;
  const open_ = new Set(shownMissing.map((item) => item.id));
  const invalid = (id: string) => open_.has(id) || id in errors;
  const done = completedPages(pages, shownMissing);
  const [current, setCurrent] = useState(0);
  const descriptors = descriptorsOf(guide);
  // DS §3.4: the sixth option reads Unassessed on every screen (the guide's print label "NOT ASSESSED" is retired here)
  const unassessed = SCORECARD_LABELS.notAssessed;
  const shown = shownRecommendation(level, draft.recommendation);
  const reco = guide?.recommendation_page;
  const atKey = (key: string | null | undefined) => (key ? pages.findIndex((page) => page.key === key) : -1);

  // The card in view follows focus (a chip, a Gate row, Tab): "Page n of N" names where the keyboard is.
  const onFocusPage = (event: FocusEvent<HTMLDivElement>) => {
    const at = atKey((event.target as Element).closest?.("[data-pb-page]")?.getAttribute("data-pb-page"));
    if (at >= 0 && at !== current) setCurrent(at);
  };
  useEffect(() => { if (current >= pages.length) setCurrent(0); }, [current, pages.length]);

  const row = (d: Dimension): RatingInput => ratingRow(draft, d) ?? { dimension_type: d.type, dimension_key: d.key, state: "unanswered" };
  const setRow = (next: RatingInput) => onChange({
    ...draft,
    ratings: draft.ratings.some((r) => r.dimension_type === next.dimension_type && r.dimension_key === next.dimension_key)
      ? draft.ratings.map((r) => (r.dimension_type === next.dimension_type && r.dimension_key === next.dimension_key ? next : r))
      : [...draft.ratings, next],
  });
  const set = (patch: Partial<Draft>) => onChange({ ...draft, ...patch });
  // Every card is on the page (the grammar's grid); a step moves focus to its card and scrolls it into view
  const goPage = (page: PageDef) => {
    setCurrent(pages.indexOf(page));
    afterPaint(() => {
      const el = typeof document !== "undefined" ? document.getElementById(page.anchor) : null;
      el?.focus({ preventScroll: true });
      el?.scrollIntoView?.({ block: "start" });
    });
  };
  /** A Gate row: move focus to the field it names (its card is on the page). */
  const onGateLink = (event: MouseEvent<HTMLElement>) => {
    const anchor = (event.target as Element | null)?.closest?.("a[href^='#']");
    const id = anchor?.getAttribute("href")?.slice(1) ?? "";
    const target = id ? document.getElementById(id) : null;
    if (!target) return;
    event.preventDefault();
    const at = atKey(target.closest("[data-pb-page]")?.getAttribute("data-pb-page"));
    if (at >= 0) setCurrent(at);
    afterPaint(() => focusField(id));
  };

  const rating = (d: Dimension, name: string) => (
    <RatingScale
      form="playbook"
      id={fieldId("rating", d)}
      name={FRAME_TEMPLATES.ratingFor({ name })}
      value={ratingValueOf(row(d))}
      descriptors={descriptors}
      unassessedLabel={unassessed}
      disabled={locked}
      invalid={invalid(fieldId("rating", d))}
      onChange={(value: RatingValue | null) => setRow(setRating(row(d), value))}
    />
  );

  const valuePages = dims.filter((d) => d.type === "value").map((d) => guide?.value_pages.find((v) => v.key === d.key) ?? fallbackValuePage(d));

  // One card per value, top to bottom question → answer: the approved questions, then the evidence (the main field),
  // the rating with its two ends, the Unassessed reason; the standard and its 5 / 1 words behind a small disclosure
  const valueCard = (d: Dimension) => {
    const page = valuePages.find((v) => v.key === d.key) ?? fallbackValuePage(d);
    const r = row(d);
    const pageDef = pages.find((p) => p.key === `value:${d.key}`)!;
    const title = page.page_title ?? page.name;
    return (
      <RecordCard key={pageDef.key} variant="form" title={title} className="mc-pbs-card mc-pbs-dimc">
        <section id={pageDef.anchor} data-pb-page={pageDef.key} data-value={d.key} tabIndex={-1} aria-label={title} className="mc-pbs-page mc-pbs-dim">
          <div className="mc-pbs-qa">
            {/* the guide's own heading for its questions when it has one (it says they are recommended, not required) */}
            <h4 className="mc-pbs-k">{guide?.questions_note || PLAYBOOK_LABELS.behavioralQuestions}</h4>
            <ol className="mc-pbs-qs">{page.questions.map((question) => <li key={question}>{question}</li>)}</ol>
          </div>
          <Disclosure sm title={PLAYBOOK_LABELS.standardAssessed} className="mc-pbs-std">
            <p className="mc-pbs-box">{page.assesses}</p>
            <Anchors page={page} />
          </Disclosure>
          <div className="mc-pbs-ans">
            <Field label={PLAYBOOK_LABELS.requiredObservations} required={!view} id={fieldId("evidence", d)} mode={view ? "view" : undefined}>
              <TextArea
                rows={EVIDENCE_ROWS}
                value={r.evidence_text ?? ""}
                onChange={(event) => setRow({ ...r, evidence_text: event.target.value })}
                invalid={invalid(fieldId("evidence", d))}
                readOnly={locked}
              />
            </Field>
            <div className="mc-pbs-rate">
              <span className="mc-pbs-k">{PLAYBOOK_LABELS.gradedScoreRating}</span>
              {rating(d, page.name)}
              <ScaleWords value={ratingValueOf(r)} descriptors={descriptors} />
              {r.state === "not_assessed" && !view
                ? <UnassessedReason d={d} row={r} editable={!locked} invalid={invalid(fieldId("not_assessed_reason", d))} onRow={setRow} />
                : null}
            </div>
          </div>
        </section>
      </RecordCard>
    );
  };

  // One card per assigned skill: its title and FIT, the strong standard behind the same small disclosure, then the
  // answer — strong observations, gaps, notes — and the score
  const skills = dims.filter((d) => d.type === "skill");
  const skillPages = skills.map((d) => guide?.skill_pages.find((s) => String(s.number) === d.key) ?? fallbackSkillPage(d, titles));
  const skillsDef = pages.find((p) => p.key === "skills");
  const skillCard = (d: Dimension, at: number) => {
    const skill = skillPages[at]!;
    const r = row(d);
    const name = skill.title || (dimensionLabel(d, titles) ?? d.key);
    const marked = invalid(fieldId("observations", d));
    const text = (key: "strong_observations" | "gaps_detected" | "notes", id: string, label: ReactNode, rows: number) => (
      <Field label={label} id={id} mode={view ? "view" : undefined}>
        <TextArea rows={rows} value={r[key] ?? ""} onChange={(event) => setRow({ ...r, [key]: event.target.value })} invalid={marked} readOnly={locked} />
      </Field>
    );
    return (
      <RecordCard
        key={`skill:${d.key}`}
        variant="form"
        title={name}
        pills={skill.fit_label ? <Pill tone="neutral" dot={false}>{skill.fit_label}</Pill> : undefined}
        className={cx("mc-pbs-card mc-pbs-dimc", marked && "mc-pbs-err")}
      >
        <section
          id={at === 0 && skillsDef ? skillsDef.anchor : `${skillsDef?.anchor ?? "pb-page-skills"}-${d.key}`}
          data-pb-page="skills"
          data-skill={d.key}
          tabIndex={-1}
          aria-label={name}
          className="mc-pbs-page mc-pbs-dim"
        >
          <Disclosure sm title={PLAYBOOK_LABELS.whatStrongLooksLike} className="mc-pbs-std">
            <p className="mc-pbs-box">{skill.what_strong_looks_like}</p>
          </Disclosure>
          <div className="mc-pbs-ans">
            {text("strong_observations", fieldId("observations", d), <span className="mc-pbs-l-ok">{PLAYBOOK_LABELS.strongObservations}</span>, 3)}
            {text("gaps_detected", `gaps-skill-${d.key}`, <span className="mc-pbs-l-red">{PLAYBOOK_LABELS.gapsDetected}</span>, 3)}
            {text("notes", `notes-skill-${d.key}`, PLAYBOOK_LABELS.notes, 5)}
            <div className="mc-pbs-rate">
              <span className="mc-pbs-k">{PLAYBOOK_LABELS.scoreGrade}</span>
              {rating(d, name)}
              <ScaleWords value={ratingValueOf(r)} descriptors={descriptors} />
            </div>
          </div>
        </section>
      </RecordCard>
    );
  };

  const rows = recoRows(level, guide, lockReason(level, guide));
  const followUp = asksFollowUp(level, draft.recommendation);
  const suggested = (
    <Stack gap={8}>
      <Field label={PLAYBOOK_LABELS.suggestedRole} required id="consider_other_role_suggested" error={errors["consider_other_role_suggested"] ?? (invalid("consider_other_role_suggested") ? V1_FIELD_WORDS.completeField : undefined)}>
        <Input value={draft.consider_other_role_suggested ?? ""} readOnly={locked} onChange={(event) => set({ consider_other_role_suggested: event.target.value })} />
      </Field>
      <Field label={PLAYBOOK_LABELS.why} optional>
        <TextArea rows={2} value={draft.consider_other_role_why ?? ""} readOnly={locked} onChange={(event) => set({ consider_other_role_why: event.target.value })} />
      </Field>
    </Stack>
  );
  const slot = (value: string): ReactNode => {
    if (value !== shown) return null;
    if (value === "consider_other_role") return suggested;
    if (value === "no_hire" && followUp) {
      return (
        <Stack gap={8}>
          <Field label={reco?.consider_other_role_prompt ?? ""} required error={invalid("consider_other_role_answer") ? V1_FIELD_WORDS.completeField : undefined}>
            <Segmented
              id="consider_other_role_answer"
              label={reco?.consider_other_role_prompt ?? undefined}
              value={draft.consider_other_role_answer ?? ""}
              mode={view ? "view" : undefined}
              options={[{ value: "yes", label: ACCESS_LABELS.Yes }, { value: "no", label: ACCESS_LABELS.No }]}
              onChange={(answer) => set({ consider_other_role_answer: answer as "yes" | "no" })}
            />
          </Field>
          {draft.consider_other_role_answer === "yes" ? suggested : null}
        </Stack>
      );
    }
    if (recommendationWord(value)?.earlierScale) {
      return (
        <Field label={V1_FIELD_WORDS.reservation} required id="reservation_text" error={invalid("reservation_text") ? V1_FIELD_WORDS.completeField : undefined}>
          <TextArea rows={2} value={draft.reservation_text ?? ""} readOnly={locked} onChange={(event) => set({ reservation_text: event.target.value })} />
        </Field>
      );
    }
    return null;
  };

  const recoDef = pages[pages.length - 1]!;
  const recommendationPage = (
    <section id={recoDef.anchor} data-pb-page={recoDef.key} tabIndex={-1} aria-labelledby={`${recoDef.anchor}-h`} className="mc-pbs-page">
      <h2 className="mc-pbs-title" id={`${recoDef.anchor}-h`}>{reco?.page_title ?? SCORECARD_LABELS.recommendation}</h2>
      <div className="mc-pbs-reco-w"><div className="mc-pbs-reco">
        <div className="mc-pbs-reco-l">
          <RecoTiles
              form="playbook"
              id="recommendation"
              options={rows}
              value={shown}
              disabled={locked}
              invalid={invalid("recommendation")}
              onChange={(value: string) => set({ recommendation: value as NonNullable<Draft["recommendation"]> })}
            >
              {slot}
            </RecoTiles>
          <PlaybookRubric
            heading={PLAYBOOK_LABELS.scoringSummaryRubric}
            scoringSummaryText={guide?.rubric.scoring_summary_text ?? PLAYBOOK_LABELS.averageCoreScore}
            average={pb.coreAverage}
            valuesScored={pb.coreValuesScored}
            ofValues={5}
            lines={guide?.rubric.lines ?? []}
            band={pb.rubricBand}
            note={guide?.rubric.bands_note ?? undefined}
          />
        </div>
        <div className="mc-pbs-reco-r">
          <Field label={FRAME_LABELS.justification} required id="justification" error={errors["justification"] ?? (invalid("justification") ? V1_FIELD_WORDS.completeField : undefined)}>
            <TextArea rows={6} value={draft.justification ?? ""} readOnly={locked} onChange={(event) => set({ justification: event.target.value })} />
          </Field>
          <Field label={FRAME_LABELS.handoff} optional id="manager_handoff">
            <TextArea rows={10} value={draft.manager_handoff ?? ""} readOnly={locked} onChange={(event) => set({ manager_handoff: event.target.value })} />
          </Field>
        </div>
      </div></div>
      {/* R1 keeps the level sentence and the Code of Conduct question although page 7 dropped them. */}
      {reco?.decision_rule_text ? <p className="mc-pbs-rule">{reco.decision_rule_text}</p> : null}
      <Field label={reco?.coc_question_text ?? ""} required error={errors["coc_answer"] ?? (invalid("coc_answer") ? V1_FIELD_WORDS.completeField : undefined)}>
        <Segmented
          id="coc_answer"
          label={reco?.coc_question_text ?? undefined}
          value={draft.coc_answer ?? ""}
          mode={view ? "view" : undefined}
          options={[{ value: "yes", label: ACCESS_LABELS.Yes }, { value: "no", label: ACCESS_LABELS.No }]}
          onChange={(answer) => set({ coc_answer: answer as "yes" | "no" })}
        />
      </Field>
      {draft.coc_answer === "no" ? (
        <Stack gap={8}>
          {reco?.coc_no_instruction ? <p className="mc-hint">{reco.coc_no_instruction}</p> : null}
          <Field label={V1_FIELD_WORDS.concernText} required id="coc_concern_text" error={invalid("coc_concern_text") ? V1_FIELD_WORDS.completeField : undefined}>
            <TextArea rows={3} value={draft.coc_concern_text ?? ""} readOnly={locked} onChange={(event) => set({ coc_concern_text: event.target.value })} />
          </Field>
          <Field label={V1_FIELD_WORDS.concernAbout} required>
            <Select
              value={draft.coc_concern_about ?? "candidate"}
              mode={view ? "view" : undefined}
              options={(Object.keys(COC_ABOUT) as (keyof typeof COC_ABOUT)[]).map((key) => ({ value: key, label: COC_ABOUT[key] }))}
              onChange={(value: string) => set({ coc_concern_about: value as NonNullable<Draft["coc_concern_about"]> })}
            />
          </Field>
        </Stack>
      ) : null}
      <div className="mc-pbs-sign">
        {hideAttestation ? null : (
          <div id="attested" className="mc-pbs-signbox">
            <span className="mc-pbs-k">{FRAME_LABELS.sign}</span>
            <Toggle
              label={reco?.attestation_text ?? FRAME_LABELS.sign}
              checked={!!draft.attested}
              blocked={view ? { reason: SCORECARD_STATUS.submitted } : undefined}
              onChange={(checked: boolean) => set({ attested: checked })}
            />
          </div>
        )}
        {pb.revisionId ? (
          <div className="mc-pbs-verify">
            <span className="mc-pbs-k">{FRAME_LABELS.recordVerification}</span>
            <Pill tone="neutral">{pb.revisionId}</Pill>
          </div>
        ) : null}
      </div>
      {reco?.footer ? <p className="mc-pbs-foot">{reco.footer}</p> : null}
    </section>
  );

  const items = gateItems(pages, shownMissing);
  const gateRows: GateRow[] = items.map((item) => ({ status: "todo", text: item.text, href: item.id ? `#${item.id}` : undefined }));
  const isSubmitted = SUBMITTED.has(open.status) && mode !== "revision";
  const pill = <Pill tone={isSubmitted ? "ok" : "info"}>{isSubmitted ? SCORECARD_STATUS.submitted : SCORECARD_STATUS.inProgress}</Pill>;

  const gate = view ? null : (
    // the bare Gate (DS row 67 `below`): Submit under Save, the open rows by card beneath it, each moving focus to its
    // field; a refusal is its own banner and its field-named words sit on the field
    <div className="mc-pbs-gate" onClick={onGateLink}>
      {refusal ? <Banner tone="red" live>{refusal}</Banner> : null}
      <Flex gap={8} wrap>
        {onSave ? <Button busy={saveBusy}  onClick={onSave}>{ACTION_LABELS.save}</Button> : null}
        {saveState}
      </Flex>
      {onSubmit ? (
        <Gate
          below
          rows={gateRows}
          ready={gateRows.length === 0}
          action={{ label: submitLabel ?? SCORECARD_PAGE_LABELS.submitScorecard, onClick: onSubmit, busy: submitBusy, hs: "score.submit" }}
        />
      ) : gateRows.length ? <Checklist>{gateRows.map((r, i) => <CheckRow key={i} status="todo" href={r.href}>{r.text}</CheckRow>)}</Checklist> : null}
    </div>
  );
  const pageTitle = [guide?.page_words?.guide_title ?? guide?.header.title, open.round_label].filter(Boolean).join(" · ");

  // F-185 (the page grammar) as Tejesh asked on 26 Oct: the steps, then ONE card grid — the guide (collapsed) across the
  // row, one question → answer card per value and per skill, then the closing cards: the recommendation with the Code
  // of Conduct and Sign, the header box with the level rule, your notes, and the Gate with Save and Submit
  return (
    <div className="mc-pbs" onFocusCapture={onFocusPage}>
      <Stepper pages={pages} current={current} done={done} status={pill} onGo={goPage} />
      {framed ? null : (
        <div className="mc-pbs-top">
          <h1 className="mc-pbs-h1">{pageTitle}</h1>
          {printAction ? <div className="mc-pbs-print">{printAction}</div> : null}
        </div>
      )}
      <CardGrid>
        {guide ? <GuideCard guide={guide} values={valuePages} skills={skillPages} descriptors={descriptors} band={level.band} reminders={reminders} /> : null}
        {dims.filter((d) => d.type === "value").map(valueCard)}
        {skills.map(skillCard)}
        <RecordCard variant="form" span={2} className="mc-pbs-card mc-pbs-close">{recommendationPage}</RecordCard>
        <RecordCard variant="summary" rows={[]} title={framed ? pageTitle : undefined} className="mc-pbs-card">
          <HeaderBox open={open} guide={guide} />
          {level.band && level.band !== "legacy" ? <LevelRule level={RULE_BAND[level.band]} met={level.met} /> : null}
          <p className="mc-hint"><b>{SCORECARD_PAGE_LABELS.independentFeedback}</b> {SCORECARD_PAGE_LABELS.noPeek}</p>
        </RecordCard>
        {notesRail ? <GridCell className="mc-pbs-rail">{notesRail}</GridCell> : null}
        {gate ? <RecordCard variant="form" span={2} className="mc-pbs-card">{gate}</RecordCard> : null}
      </CardGrid>
    </div>
  );
}
